import { compareDisplayNames } from '@utils/string.util';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';

import { SelectionModel } from '@angular/cdk/collections';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute } from '@angular/router';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { AccountResponse } from '@entities/account';
import { ApiError } from '@errors/error';
import {
  DeviceSettingGroupResponse,
  DeviceSettingGroupUpdateRequest,
  DeviceSettingListResponse,
  DeviceSettingTagUpdateRequest,
} from '@entities/deviceSetting';
import { DeviceSettingService } from '@services/api/device-setting.service';
import {
  convertToAssetTypeList,
  convertToCommonDeviceSetting,
  convertToDeviceSettingRequest,
} from '@utils/device-setting.util';
import { Subscription, firstValueFrom, interval } from 'rxjs';
import { exhaustMap } from 'rxjs/operators';
import {
  DeviceGroupBulkSettingErrorDialogComponent,
  DeviceGroupBulkSettingErrorDialogOption,
} from './device-group-bulk-setting-error-dialog/device-group-bulk-setting-error-dialog.component';
import {
  DeviceGroupDetailEditDialogComponent,
  DeviceGroupDialogOption,
} from './device-group-detail-edit-dialog/device-group-detail-edit-dialog.component';
import {
  DeviceContractViewModel,
  DeviceSettingGroupDialogOption,
  DeviceSettingGroupEditDialogComponent,
  DeviceSettingGroupEditViewModel,
  DeviceTypeOption,
  DeviceViewModel,
} from './device-group-edit-dialog/device-group-edit-dialog.component';
import {
  DeviceGroupListTableComponent,
  DeviceGroupSettingDetailViewModel,
  DeviceSettingGroupViewModel,
} from './device-group-list-table/device-group-list-table.component';
import {
  AssettypeViewModel,
  DeviceSettingEditDialogComponent,
  DeviceTypeViewModel,
  DialogOption,
} from './device-setting-edit-dialog/device-setting-edit-dialog.component';
import {
  DeviceSettingListTableComponent,
  DeviceSettingViewModel,
  DeviceTelemetryViewModel,
  DeviceNameUpdate,
} from './device-setting-list-table/device-setting-list-table.component';
import {
  DeviceTagEditDialogComponent,
  DeviceTagViewModel,
  TagDialogOption,
} from './device-tag-list/device-tag-edit-dialog.component';

/** 権限 */
export type Role =
  | 'system_admin'
  | 'tenant_admin'
  | 'tenant_editor'
  | 'tenant_viewer';

/** デバイス指定タイプ */
export type DeviceSelectType = 'tag' | 'direct';

/** タブ */
export type DisplayTypeViewModel = 'list' | 'groupSetting';

@Component({
  selector: 'app-device-setting-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonToggleModule,
    DeviceSettingListTableComponent,
    DeviceGroupListTableComponent,
    MatCardModule,
  ],
  templateUrl: './device-setting-list.component.html',
  styleUrl: './device-setting-list.component.scss',
})
export class DeviceSettingListComponent implements OnInit, OnDestroy {
  private static readonly BULK_SETTING_POLLING_INTERVAL_MS = 10_000;

  /** 権限 */
  public role: Role = 'system_admin';

  /** デバイス設定 */
  public deviceSetting?: DeviceSettingViewModel;

  /** デバイスグループ設定詳細 */
  public deviceGroupSettingDetail?: DeviceGroupSettingDetailViewModel;

  /** デバイス設定グループ */
  public deviceSettingGroupEdit?: DeviceSettingGroupEditViewModel;

  /** デバイス関連タグ */
  public deviceTag: DeviceTagViewModel = {
    deviceTagRefsList: [],
    tagList: [],
  };

  /** デバイス関連タグ一覧 */
  public deviceTagRefsList: string[] = [];

  /** デバイス設定一覧 */
  public deviceSettingList: DeviceTelemetryViewModel[] = [];

  /** デバイス設定グループ一覧 */
  public deviceSettingGroupList: DeviceSettingGroupViewModel[] = [];

  /** アセットタイプ一覧 */
  public assettypeList: AssettypeViewModel[] = [];

  /** デバイス種類 */
  public deviceType?: DeviceTypeViewModel;

  /** デバイス種類の選択肢 */
  public deviceTypeDisplayNames: string[] = [];

  /** 設定グループのデバイス種類表示名一覧 */
  public groupDeviceTypeDisplayNames: string[] = [];

  /** 選択状態 */
  public selection = new SelectionModel<DeviceTelemetryViewModel>(true, []);

  /** 所属テナント */
  public currentTenant = '';

  /** タブ */
  public selectedDisplayType: DisplayTypeViewModel = 'list';

  /** 一括設定ステータスポーリング */
  private bulkSettingPollingSubscription?: Subscription;

  constructor(
    private deviceSettingService: DeviceSettingService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    // ログインユーザーの権限を取得する
    const loggedInUser: AccountResponse =
      this.route.snapshot.data['loggedInUser'];
    this.role = loggedInUser.tenant.roleId;

    // デバイス設定一覧を取得する
    this._initDeviceSettingList();
    // テナント管理者のときのみデバイスグループ設定一覧を取得する
    if (this.role === 'tenant_admin') {
      this._initDeviceSettingGroupList();
    }
  }

  public ngOnDestroy(): void {
    this.bulkSettingPollingSubscription?.unsubscribe();
    this.bulkSettingPollingSubscription = undefined;
  }

  public async updateDeviceName(update: DeviceNameUpdate): Promise<void> {
    let saved = false;
    try {
      await firstValueFrom(
        this.deviceSettingService.updateName(update.device.serialNo, {
          deviceName: update.deviceName,
        }),
      );
      saved = true;
      this._initDeviceSettingList();
    } catch (error) {
      if (
        error instanceof ApiError &&
        [401, 403].includes(error.response.status)
      ) {
        throw error;
      }
      const duplicate =
        error instanceof ApiError &&
        error.response.status === 409 &&
        error.response.error?.detail?.parameters?.message ===
          'device_name_already_exists';
      this.showDeviceNameError(
        duplicate
          ? 'デバイス名が重複しています。別の名前でもう一度お試しください。'
          : 'デバイス名の変更に失敗しました。しばらくしてからもう一度お試しください。',
      );
    } finally {
      update.complete(saved);
    }
  }

  private showDeviceNameError(message: string): void {
    this.dialog.open(AlertDialogComponent, {
      width: '600px',
      data: { type: 'error', message } as AlertDialogOption,
    });
  }

  /**
   * デバイス設定詳細ダイアログを表示する
   * @param serialNo シリアル番号
   */
  public async openDeviceSettingDetailEditDialog(
    device: DeviceTelemetryViewModel,
  ) {
    const serialNo = device.serialNo;
    this.deviceSettingService.find(serialNo).subscribe({
      next: async (deviceSetting) => {
        this.deviceSetting = {
          serialNo: deviceSetting.serialNo,
          deviceName: deviceSetting.deviceName || deviceSetting.serialNo,
          status: deviceSetting.status,
          lastUpdatedAt: deviceSetting.lastUpdatedAt,
          plannedUpdatedAt: deviceSetting.plannedUpdatedAt,
          battery: deviceSetting.battery,
          ...convertToCommonDeviceSetting(deviceSetting),
        };
        const assettypeList = deviceSetting.assettypeList;
        this.assettypeList = convertToAssetTypeList(assettypeList);

        this.deviceType = deviceSetting.deviceType;

        let battery = null;
        if (device.battery) {
          battery = Number(device.battery);
        }

        let isSettingGroupName = false;
        const targetSettingGroupName = device.settingGroupName;
        if (targetSettingGroupName) {
          isSettingGroupName = this.deviceSettingList.some(
            (device) => device.settingGroupName === targetSettingGroupName,
          );
        }

        const options: DialogOption = {
          updating: false,
          deviceName: this.deviceSetting.deviceName || '',
          serialNo: this.deviceSetting.serialNo,
          status: device.status,
          lastUpdatedAt: device.lastCommunicationAt
            ? new Date(device.lastCommunicationAt)
            : null,
          plannedUpdatedAt: new Date(),
          battery: battery,
          assettypeList: this.assettypeList,
          deviceType: this.deviceType,
          isSettingGroupName: isSettingGroupName,
          contractExpiredAt: deviceSetting.deviceContract.contractExpiredAt,
          shipmentTrackingEditable: deviceSetting.shipmentTrackingEditable,
        };

        // 更新時
        if (this.deviceSetting) {
          options.updating = true;
          options.plannedUpdatedAt = device.lastCommunicationAt
            ? new Date(
                new Date(device.lastCommunicationAt).getTime() +
                  this.deviceSetting.reportingFrequency * 60000,
              )
            : null;
          options.target = {
            deviceName: this.deviceSetting.deviceName,
            samplingFrequency: this.deviceSetting.samplingFrequency,
            reportingFrequency: this.deviceSetting.reportingFrequency,
            shipmentTracking: this.deviceSetting.shipmentTracking,
            locationDetected: this.deviceSetting.locationDetected,
            locationPriority: [
              this.deviceSetting.locationPriority?.[0] || '',
              this.deviceSetting.locationPriority?.[1] || '',
              this.deviceSetting.locationPriority?.[2] || '',
            ],
            fallDetected: this.deviceSetting.fallDetected,
            mcdDetected: this.deviceSetting.mcdDetected,
            lightExposureDetected: this.deviceSetting.lightExposureDetected,
            lightExposureDetectValue:
              this.deviceSetting.lightExposureDetectValue,
            shockDetected: this.deviceSetting.shockDetected,
            shockDetectedDetectValue:
              this.deviceSetting.shockDetectedDetectValue,
            temperature: this.deviceSetting.temperature,
            temperatureSettings: [
              this.deviceSetting.temperatureSettings?.[0] || 0,
              this.deviceSetting.temperatureSettings?.[1] || 0,
              this.deviceSetting.temperatureSettings?.[2] || 0,
              this.deviceSetting.temperatureSettings?.[3] || 0,
            ],
            pressure: this.deviceSetting.pressure,
            pressureSettings: [
              this.deviceSetting.pressureSettings?.[0] || 0,
              this.deviceSetting.pressureSettings?.[1] || 0,
              this.deviceSetting.pressureSettings?.[2] || 0,
              this.deviceSetting.pressureSettings?.[3] || 0,
            ],
            humidity: this.deviceSetting.humidity,
            humiditySettings: [
              this.deviceSetting.humiditySettings?.[0] || 0,
              this.deviceSetting.humiditySettings?.[1] || 0,
              this.deviceSetting.humiditySettings?.[2] || 0,
              this.deviceSetting.humiditySettings?.[3] || 0,
            ],
            tilt: this.deviceSetting.tilt,
            tiltSettings: [
              this.deviceSetting.tiltSettings?.[0] || 0,
              this.deviceSetting.tiltSettings?.[1] || 0,
              this.deviceSetting.tiltSettings?.[2] || 0,
              this.deviceSetting.tiltSettings?.[3] || 0,
            ],
            light: this.deviceSetting.light,
            lightSettings: [
              this.deviceSetting.lightSettings?.[0] || 0,
              this.deviceSetting.lightSettings?.[1] || 0,
              this.deviceSetting.lightSettings?.[2] || 0,
              this.deviceSetting.lightSettings?.[3] || 0,
            ],
            fmd: this.deviceSetting.fmd,
          };
        }

        const editedDeviceSetting: DeviceSettingViewModel =
          await firstValueFrom(
            this.dialog
              .open(DeviceSettingEditDialogComponent, {
                width: '90%',
                maxWidth: '1500px',
                data: options,
              })
              .afterClosed(),
          );

        if (!editedDeviceSetting) {
          return;
        }

        const deviceSettingRequest =
          convertToDeviceSettingRequest(editedDeviceSetting);
        deviceSettingRequest.deviceName = editedDeviceSetting.deviceName || '';

        const isDuplicateDeviceName = this.deviceSettingList.some(
          (deviceSetting) =>
            deviceSetting.serialNo !== serialNo &&
            deviceSetting.deviceName === deviceSettingRequest.deviceName,
        );
        if (isDuplicateDeviceName) {
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: `デバイス名が重複しているため、${
              options.updating ? '更新' : '追加'
            }に失敗しました。\n別の名前でもう一度お試しください。`,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
          return;
        }

        this.deviceSettingService
          .update(serialNo, deviceSettingRequest)
          .subscribe({
            next: () => {
              this._initDeviceSettingList();
              if (this.role === 'tenant_admin') {
                this._initDeviceSettingGroupList();
              }
              this.selection.clear();
            },
            error: (err) => {
              // サーバ側で設定した識別子で判定
              const isDuplicateDeviceName =
                err?.response?.status === 409 &&
                err?.response?.error?.detail?.parameters?.message ===
                  'device_name_already_exists';

              const alertDialogOptions: AlertDialogOption = {
                type: 'error',
                message: isDuplicateDeviceName
                  ? `デバイス名が重複しているため、デバイス名の更新に失敗しました。\n別の名前でもう一度お試しください。`
                  : `デバイスの変更に失敗しました。\nしばらくしてからもう一度お試しください。`,
              };
              this.dialog.open(AlertDialogComponent, {
                width: '600px',
                data: alertDialogOptions,
              });
            },
          });
      },
    });
  }

  /**
   * タグ設定ダイアログを表示する
   * @param deviceList デバイス一覧
   */
  public async openDeviceTagEditDialog(deviceList: DeviceTelemetryViewModel[]) {
    let serialNoList: string[] = [];

    let deviceName = '';
    if (deviceList.length <= 0) {
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message: `デバイスを選択ください。`,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
      return;
    } else if (deviceList.length <= 1) {
      deviceName = deviceList[0].deviceName;
    }
    for (const device of deviceList) {
      if (device.serialNo !== '') {
        serialNoList.push(device.serialNo);
      }
    }
    serialNoList = [...new Set(serialNoList)];

    this.deviceSettingService.findTagList(serialNoList).subscribe({
      next: async (deviceTagSetting) => {
        this.deviceTag = {
          deviceTagRefsList: deviceTagSetting.deviceTagRefsList,
          tagList: deviceTagSetting.tagList,
        };

        const options: TagDialogOption = {
          deviceName: deviceName,
        };

        let deviceTagList: { [key: string]: string }[] = [];
        for (const deviceTagRefs of this.deviceTag.deviceTagRefsList) {
          const tmpDeviceTag: { [key: string]: string } = {
            tagId: deviceTagRefs['tagId'],
            tagName: deviceTagRefs['tagName'],
            color: deviceTagRefs['color'],
          };
          deviceTagList.push(tmpDeviceTag);
        }

        const deviceTag: { [key: string]: string } = {};
        for (const deviceTagRefs of deviceTagList) {
          deviceTag[deviceTagRefs['tagId']] = deviceTagRefs['tagName'];
        }

        const keys = new Set();
        deviceTagList = deviceTagList.filter((item) => {
          const key = JSON.stringify(item);
          const isDuplicate = keys.has(key);
          keys.add(key);
          return !isDuplicate;
        });

        let filteredTagList = this.deviceTag.tagList.filter(
          (tag) => !Object.keys(deviceTag).includes(tag['tagId']),
        );
        filteredTagList = filteredTagList.sort((first, second) =>
          first['tagName'] > second['tagName'] ? 1 : -1,
        );
        deviceTagList = deviceTagList.sort((first, second) =>
          first['tagName'] > second['tagName'] ? 1 : -1,
        );
        if (this.deviceTag) {
          options.target = {
            tagList: filteredTagList,
            deviceTagRefsList: deviceTagList,
          };
        }

        const editedDeviceTag: DeviceTagViewModel = await firstValueFrom(
          this.dialog
            .open(DeviceTagEditDialogComponent, {
              width: '90%',
              maxWidth: '1000px',
              data: options,
            })
            .afterClosed(),
        );

        if (!editedDeviceTag) {
          return;
        }

        let deviceTagRef: { [key: string]: string };
        const tagList = [];
        for (const deviceTag of editedDeviceTag.tagList) {
          tagList.push(deviceTag['tagId']);
        }

        const deviceTagRefsList: { [key: string]: string }[] = [];
        if (!editedDeviceTag.deviceTagRefsList.length) {
          for (const serialNo of serialNoList) {
            deviceTagRef = {
              tag_id: '',
              device_id: serialNo,
            };
            deviceTagRefsList.push(deviceTagRef);
          }
        } else {
          for (const device of editedDeviceTag.deviceTagRefsList) {
            for (const serialNo of serialNoList) {
              deviceTagRef = {
                tag_id: device['tagId'],
                device_id: serialNo,
              };
              deviceTagRefsList.push(deviceTagRef);
            }
          }
        }

        const deviceTagRequest: DeviceSettingTagUpdateRequest = {
          device_tag_refs_list: deviceTagRefsList,
        };

        this.deviceSettingService.updateTag(deviceTagRequest).subscribe({
          next: () => {
            this._initDeviceSettingList();
            if (this.role === 'tenant_admin') {
              this._initDeviceSettingGroupList();
            }
            this.selection.clear();
          },
          error: () => {
            const alertDialogOptions: AlertDialogOption = {
              type: 'error',
              message: `タグ設定に失敗しました。\nしばらくしてからもう一度お試しください。`,
            };
            this.dialog.open(AlertDialogComponent, {
              width: '600px',
              data: alertDialogOptions,
            });
          },
        });
      },
    });
  }

  /**
   * 一括設定エラー詳細ダイアログを表示する
   * @param deviceSettingGroup 対象のデバイス設定グループ
   */
  public openBulkSettingErrorDialog(
    deviceSettingGroup: DeviceSettingGroupViewModel,
  ): void {
    const options: DeviceGroupBulkSettingErrorDialogOption = {
      message: `設定グループ「${deviceSettingGroup.deviceSettingGroupName}」の一括設定で発生したエラー情報は以下の通りです。`,
      data: deviceSettingGroup.bulkSettingErrorList ?? [],
    };

    this.dialog.open(DeviceGroupBulkSettingErrorDialogComponent, {
      width: '900px',
      data: options,
    });
  }

  /**
   * デバイス設定グループ詳細設定ダイアログを表示する
   * @param groupId 設定グループID
   * @param deviceTypeId デバイス種類ID
   */
  public async openDeviceGroupSettingDetailEditDialog(
    groupId: string | undefined,
    deviceTypeKey: string | undefined,
  ) {
    if (groupId === undefined || deviceTypeKey == undefined) {
      return;
    }
    this.deviceSettingService
      .findDeviceGroupSetting(groupId, deviceTypeKey)
      .subscribe({
        next: async (deviceGroupSettingDetail) => {
          this.deviceGroupSettingDetail = {
            deviceSettingGroupId:
              deviceGroupSettingDetail.deviceSettingGroup.deviceSettingGroupId,
            deviceSettingGroupName:
              deviceGroupSettingDetail.deviceSettingGroup
                .deviceSettingGroupName,
              deviceTypeId: deviceGroupSettingDetail.deviceType.deviceTypeId,
            allExpired: deviceGroupSettingDetail.allExpired,
            ...convertToCommonDeviceSetting(deviceGroupSettingDetail),
          };
          const assettypeList = deviceGroupSettingDetail.assettypeList;
          this.assettypeList = convertToAssetTypeList(assettypeList);

          this.deviceType = deviceGroupSettingDetail.deviceType;

          const options: DeviceGroupDialogOption = {
            updating: true,
            deviceSettingGroupId:
              this.deviceGroupSettingDetail.deviceSettingGroupId,
            deviceSettingGroupName:
              this.deviceGroupSettingDetail.deviceSettingGroupName,
            assettypeList: this.assettypeList,
            deviceType: this.deviceType,
            deviceTypeId: this.deviceGroupSettingDetail.deviceTypeId || '',
            contractExpiredAt:
              deviceGroupSettingDetail.deviceContract.contractExpiredAt,
          };

          if (this.deviceGroupSettingDetail) {
            options.target = {
              samplingFrequency:
                this.deviceGroupSettingDetail.samplingFrequency,
              reportingFrequency:
                this.deviceGroupSettingDetail.reportingFrequency,
              shipmentTracking: this.deviceGroupSettingDetail.shipmentTracking,
              locationDetected: this.deviceGroupSettingDetail.locationDetected,
              locationPriority: [
                this.deviceGroupSettingDetail.locationPriority?.[0] || '',
                this.deviceGroupSettingDetail.locationPriority?.[1] || '',
                this.deviceGroupSettingDetail.locationPriority?.[2] || '',
              ],
              fallDetected: this.deviceGroupSettingDetail.fallDetected,
              mcdDetected: this.deviceGroupSettingDetail.mcdDetected,
              lightExposureDetected:
                this.deviceGroupSettingDetail.lightExposureDetected,
              lightExposureDetectValue:
                this.deviceGroupSettingDetail.lightExposureDetectValue,
              shockDetected: this.deviceGroupSettingDetail.shockDetected,
              shockDetectedDetectValue:
                this.deviceGroupSettingDetail.shockDetectedDetectValue,
              temperature: this.deviceGroupSettingDetail.temperature,
              temperatureSettings: [
                this.deviceGroupSettingDetail.temperatureSettings?.[0] || 0,
                this.deviceGroupSettingDetail.temperatureSettings?.[1] || 0,
                this.deviceGroupSettingDetail.temperatureSettings?.[2] || 0,
                this.deviceGroupSettingDetail.temperatureSettings?.[3] || 0,
              ],
              pressure: this.deviceGroupSettingDetail.pressure,
              pressureSettings: [
                this.deviceGroupSettingDetail.pressureSettings?.[0] || 0,
                this.deviceGroupSettingDetail.pressureSettings?.[1] || 0,
                this.deviceGroupSettingDetail.pressureSettings?.[2] || 0,
                this.deviceGroupSettingDetail.pressureSettings?.[3] || 0,
              ],
              humidity: this.deviceGroupSettingDetail.humidity,
              humiditySettings: [
                this.deviceGroupSettingDetail.humiditySettings?.[0] || 0,
                this.deviceGroupSettingDetail.humiditySettings?.[1] || 0,
                this.deviceGroupSettingDetail.humiditySettings?.[2] || 0,
                this.deviceGroupSettingDetail.humiditySettings?.[3] || 0,
              ],
              tilt: this.deviceGroupSettingDetail.tilt,
              tiltSettings: [
                this.deviceGroupSettingDetail.tiltSettings?.[0] || 0,
                this.deviceGroupSettingDetail.tiltSettings?.[1] || 0,
                this.deviceGroupSettingDetail.tiltSettings?.[2] || 0,
                this.deviceGroupSettingDetail.tiltSettings?.[3] || 0,
              ],
              light: this.deviceGroupSettingDetail.light,
              lightSettings: [
                this.deviceGroupSettingDetail.lightSettings?.[0] || 0,
                this.deviceGroupSettingDetail.lightSettings?.[1] || 0,
                this.deviceGroupSettingDetail.lightSettings?.[2] || 0,
                this.deviceGroupSettingDetail.lightSettings?.[3] || 0,
              ],
              fmd: this.deviceGroupSettingDetail?.fmd,
              allExpired: this.deviceGroupSettingDetail.allExpired,
            };
          }

          const editedDeviceGroupSettingDetail: DeviceGroupSettingDetailViewModel =
            await firstValueFrom(
              this.dialog
                .open(DeviceGroupDetailEditDialogComponent, {
                  width: '90%',
                  maxWidth: '1300px',
                  data: options,
                })
                .afterClosed(),
            );

          if (!editedDeviceGroupSettingDetail) {
            return;
          }

          const deviceGroupSettingRequest = convertToDeviceSettingRequest(
            editedDeviceGroupSettingDetail,
          );
          deviceGroupSettingRequest.deviceSettingGroup = {
            deviceSettingGroupId:
              editedDeviceGroupSettingDetail.deviceSettingGroupId,
            deviceSettingGroupName:
              editedDeviceGroupSettingDetail.deviceSettingGroupName,
          };

          deviceGroupSettingRequest.deviceType = {
            deviceTypeId: editedDeviceGroupSettingDetail.deviceTypeId || '',
          };

          this.deviceSettingService
            .updateGroupedSetting(groupId, deviceGroupSettingRequest)
            .subscribe({
              next: () => {
                this._initDeviceSettingList();
                if (this.role === 'tenant_admin') {
                  this._initDeviceSettingGroupList();
                }
              },
              error: () => {
                const alertDialogOptions: AlertDialogOption = {
                  type: 'error',
                  message: `デバイス更新に失敗しました。\nしばらくしてからもう一度お試しください。`,
                };
                this.dialog.open(AlertDialogComponent, {
                  width: '600px',
                  data: alertDialogOptions,
                });
              },
            });
        },
      });
  }

  /**
   * デバイス設定グループ新規登録・編集ダイアログを表示する。
   */
  public async openDeviceSettingGroupEditDialog(
    deviceSettingGroup?: DeviceSettingGroupEditViewModel,
  ) {
    const groupId = deviceSettingGroup?.deviceSettingGroupId || '';
    let updating: boolean = false;
    if (deviceSettingGroup) {
      updating = true;
      this.deviceSettingService.findGroupDeviceList(groupId).subscribe({
        next: async (groupDeviceListResponse) => {
          const deviceList: DeviceViewModel[] = [];
          for (const device of groupDeviceListResponse.deviceList) {
            const deviceContractViewModel: DeviceContractViewModel = {
              deviceId: device.deviceContract?.deviceId || '',
              tenantId: device.deviceContract?.tenantId || '',
              lpnId: device.deviceContract?.lpnId || '',
              contractType: device.deviceContract?.contractType || '',
              contractExpiredAt: device.deviceContract?.contractExpiredAt || '',
              contractStartedAt: device.deviceContract?.contractStartedAt || '',
            };

            deviceList.push({
              deviceId: device.deviceId,
              deviceName: device.deviceName,
              deviceTypeId: device.deviceTypeId,
              deviceTypeKey: device.deviceTypeKey,
              deviceTypeDisplayName: device.deviceTypeDisplayName,
              tenantId: device.tenantId,
              deviceContract: deviceContractViewModel,
            });
          }

          const groupDeviceList: DeviceViewModel[] = [];
          for (const device of groupDeviceListResponse.groupDeviceList) {
            const deviceContractViewModel: DeviceContractViewModel = {
              deviceId: device.deviceContract?.deviceId || '',
              tenantId: device.deviceContract?.tenantId || '',
              lpnId: device.deviceContract?.lpnId || '',
              contractType: device.deviceContract?.contractType || '',
              contractExpiredAt: device.deviceContract?.contractExpiredAt || '',
              contractStartedAt: device.deviceContract?.contractStartedAt || '',
            };

            groupDeviceList.push({
              deviceId: device.deviceId,
              deviceName: device.deviceName,
              deviceTypeId: device.deviceTypeId,
              deviceTypeKey: device.deviceTypeKey,
              deviceTypeDisplayName: device.deviceTypeDisplayName,
              tenantId: device.tenantId,
              deviceContract: deviceContractViewModel,
            });
          }

          this.editDeviceSettingGroup(
            groupId,
            groupDeviceListResponse.deviceSettingGroupName,
            groupDeviceListResponse.description,
            groupDeviceListResponse.deviceTypeId,
            groupDeviceListResponse.deviceTypeKey || '',
            updating,
            deviceList,
            groupDeviceList,
          );
        },
      });
    } else {
      // デバイス一覧を取得する
      this.deviceSettingService.findDeviceList().subscribe({
        next: async (deviceListResponse) => {
          const deviceList: DeviceViewModel[] = deviceListResponse.map(
            (device) => ({
              deviceId: device.deviceId,
              deviceName: device.deviceName,
              deviceTypeId: device.deviceTypeId,
              deviceTypeKey: device.deviceTypeKey,
              deviceTypeDisplayName: device.deviceTypeDisplayName,
              tenantId: device.tenantId,
              deviceContract: device.deviceContract,
            }),
          );
          this.editDeviceSettingGroup(
            groupId,
            '',
            '',
            '',
            '',
            updating,
            deviceList,
            [],
          );
        },
      });
    }
  }

  /**
   * デバイス設定グループを削除する
   * @param deviceList デバイス一覧
   */
  public async deleteDeviceSettingGroup(
    deviceSettingGroup?: DeviceSettingGroupViewModel,
  ) {
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `選択したデバイス設定グループを削除してよろしいですか？`,
      operation: '削除',
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    if (!deviceSettingGroup?.deviceSettingGroupId) {
      return;
    }

    this.deviceSettingService
      .deleteDeviceSettingGroup(deviceSettingGroup?.deviceSettingGroupId)
      .subscribe({
        next: () => {
          this._initDeviceSettingList();
          if (this.role === 'tenant_admin') {
            this._initDeviceSettingGroupList();
          }
        },
        error: (error) => {
          let message = '';
          if (
            error.response.status == 400 &&
            error.response.error.detail.type == 'NotFoundWebException'
          ) {
            message =
              '他のユーザーによってデータが変更されたため、更新に失敗しました。最新のデータを取得して再度試してください。';
          } else {
            message =
              'デバイス削除に失敗しました。\nしばらくしてからもう一度お試しください。';
          }
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: message,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
  }
  /**
   * 第一ソートキーを最終更新日時/第二ソートキーをシリアル番号としてソートする
   * デバイスIDは上から下に昇順で表示。最終更新日時は上から下に降順で表示。
   */
  private _sortDeviceSettingList(
    deviceSettingList: DeviceSettingListResponse[],
  ) {
    deviceSettingList.sort((a, b) => {
      if (a.lastCommunicationAt === null && b.lastCommunicationAt === null) {
        return a.deviceId.localeCompare(b.deviceId);
      } else if (a.lastCommunicationAt === null) {
        return 1;
      } else if (b.lastCommunicationAt === null) {
        return -1;
      } else {
        return (
          new Date(b.lastCommunicationAt).getTime() -
          new Date(a.lastCommunicationAt).getTime()
        );
      }
    });
  }

  /**
   * デバイス設定の一覧を取得する。
   */
  private _initDeviceSettingList() {
    this.deviceSettingService.findAll().subscribe({
      next: (deviceSettingList) => {
        this._sortDeviceSettingList(deviceSettingList);

        this.deviceSettingList = deviceSettingList.map((deviceSetting) => ({
          deviceName: deviceSetting.deviceName || deviceSetting.deviceId,
          serialNo: deviceSetting.deviceId,
          deviceTypeId: deviceSetting.deviceTypeId,
          deviceTypeDisplayName: deviceSetting.deviceTypeDisplayName || '',
          lastCommunicationAt: deviceSetting.lastCommunicationAt,
          status: deviceSetting.validContractState
            ? deviceSetting.status
            : '契約終了',
          battery: deviceSetting.battery,
          settingGroupName: deviceSetting.deviceSettingGroupName,
          tagList: [
            ...(deviceSetting.tagList ||
              deviceSetting.tagNameList.map((tagName) => ({
                tagId: tagName,
                tagName,
              }))),
          ].sort((first, second) =>
            first.tagName.localeCompare(second.tagName),
          ),
          tag: deviceSetting.tagNameList.sort((first, second) =>
            first > second ? 1 : -1,
          ),
          validContractState: deviceSetting.validContractState,
        }));
        this.deviceTypeDisplayNames = Array.from(
          new Set(
            deviceSettingList.map(
              (setting) => setting.deviceTypeDisplayName || '',
            ),
          ),
        )
          .filter(Boolean)
          .sort(compareDisplayNames);
      },
    });
  }

  /**
   * デバイス設定グループの一覧を取得する。
   */
  private _initDeviceSettingGroupList() {
    this.deviceSettingService.findDeviceSettingGroupList().subscribe({
      next: (deviceSettingGroupList) => {
        this._setDeviceSettingGroupList(deviceSettingGroupList);
      },
    });
  }

  private _setDeviceSettingGroupList(
    deviceSettingGroupList: DeviceSettingGroupResponse[],
  ) {
    this.deviceSettingGroupList = deviceSettingGroupList.map(
      (deviceSettingGroup) => ({
        deviceSettingGroupId: deviceSettingGroup.deviceSettingGroupId,
        deviceSettingGroupName: deviceSettingGroup.deviceSettingGroupName,
              deviceTypeId: deviceSettingGroup.deviceTypeId,
        deviceTypeKey: deviceSettingGroup.deviceTypeKey,
        deviceTypeDisplayName: deviceSettingGroup.deviceTypeDisplayName,
        description: deviceSettingGroup.description,
        lastUpdatedAt: deviceSettingGroup.updatedAt,
        bulkSettingStatus: deviceSettingGroup.bulkSettingStatus,
        bulkSettingProgress: deviceSettingGroup.bulkSettingProgress,
        bulkSettingErrorList: deviceSettingGroup.bulkSettingErrorList,
        deviceCount: deviceSettingGroup.groupDeviceCount,
        isAllContractDevicesBulkApplied:
          deviceSettingGroup.isAllContractDevicesBulkApplied,
      }),
    );

    let filteredDeviceSettingGroupList: DeviceSettingGroupViewModel[] = [];
    const groups = this.groupList(
      this.deviceSettingGroupList,
      (deviceSettingGroup) => deviceSettingGroup.deviceSettingGroupId,
    );
    Object.entries(groups).forEach(([, deviceSettingGroupList]) => {
      if (deviceSettingGroupList) {
        filteredDeviceSettingGroupList = filteredDeviceSettingGroupList.concat(
          deviceSettingGroupList.filter(
            (value, index, self) =>
              index ===
              self.findIndex((t) => t.deviceTypeKey === value.deviceTypeKey),
          ),
        );
      }
    });

    // 全ての処理が完了した後でデバイスリストを更新
    this.deviceSettingGroupList = filteredDeviceSettingGroupList;
    this.sortDeviceSettingGroupList(this.deviceSettingGroupList);

    // デバイス種類のリストを取得
    const displayNames = deviceSettingGroupList.map(
      (group) => group.deviceTypeDisplayName || '',
    );
    this.groupDeviceTypeDisplayNames = Array.from(
      new Set(displayNames),
    )
      .filter(Boolean)
      .sort(compareDisplayNames);

    // ポーリングして状態を確認
    this._updateBulkSettingPollingState();
  }

  private _updateBulkSettingPollingState(): void {
    // 進行中のステータスがあれば、ポーリングを行う
    if (this._hasInProgressBulkSetting()) {
      this._startBulkSettingPolling();
    } else {
      this.bulkSettingPollingSubscription?.unsubscribe();
      this.bulkSettingPollingSubscription = undefined;
    }
  }

  private _hasInProgressBulkSetting(): boolean {
    return this.deviceSettingGroupList.some(
      (deviceSettingGroup) =>
        deviceSettingGroup.bulkSettingStatus === 'IN_PROGRESS',
    );
  }

  private _startBulkSettingPolling(): void {
    if (this.bulkSettingPollingSubscription) {
      return;
    }

    this.bulkSettingPollingSubscription = interval(
      DeviceSettingListComponent.BULK_SETTING_POLLING_INTERVAL_MS,
    )
      .pipe(
        exhaustMap(() =>
          this.deviceSettingService.findDeviceSettingGroupList(true),
        ),
      )
      .subscribe({
        next: (deviceSettingGroupList) => {
          this._setDeviceSettingGroupList(deviceSettingGroupList);
        },
      });
  }

  /** デバイス設定グループの編集もしくは追加を行う。 */
  private async editDeviceSettingGroup(
    groupId: string,
    deviceSettingGroupName: string,
    description: string,
    deviceTypeId: string,
    deviceTypeKey: string,
    updating: boolean,
    deviceList: DeviceViewModel[],
    groupDeviceList: DeviceViewModel[],
  ) {
    deviceList = deviceList.sort((first, second) =>
      first.deviceName > second.deviceName ? 1 : -1,
    );
    groupDeviceList = groupDeviceList.sort((first, second) =>
      first.deviceName > second.deviceName ? 1 : -1,
    );
    const targetDeviceSettingGroup: DeviceSettingGroupEditViewModel = {
      deviceSettingGroupId: groupId,
      deviceSettingGroupName: deviceSettingGroupName,
      deviceTypeId: deviceTypeId,
      deviceTypeKey,
      description: description,
      groupDeviceList: groupDeviceList,
      deviceList: deviceList,
    };

    const deviceTypeList: DeviceTypeOption[] = [...deviceList, ...groupDeviceList]
      .filter((device) => !!device.deviceTypeKey)
      .map((device) => ({
        deviceTypeKey: device.deviceTypeKey || '',
        deviceTypeId: device.deviceTypeId,
        displayName: device.deviceTypeDisplayName || '',
      }))
      .filter((deviceType) => !!deviceType.displayName)
      .filter((deviceType, index, self) =>
        index === self.findIndex((value) => value.deviceTypeKey === deviceType.deviceTypeKey));
    const options: DeviceSettingGroupDialogOption = {
      updating: updating,
      deviceTypeList,
      target: targetDeviceSettingGroup,
    };

    const editedDeviceSettingGroup: DeviceSettingGroupEditViewModel =
      await firstValueFrom(
        this.dialog
          .open(DeviceSettingGroupEditDialogComponent, {
            width: '90%',
            maxWidth: '1300px',
            data: options,
          })
          .afterClosed(),
      );

    if (!editedDeviceSettingGroup) {
      return;
    }

    // 自設定グループを除いた設定グループの一覧を作成する
    // 後続の設定グループ名の重複判定で使用するため
    const tmpDeviceSettingGroupList = this.deviceSettingGroupList.filter(
      (deviceSettingGroup) =>
        deviceSettingGroup.deviceSettingGroupId !==
        editedDeviceSettingGroup.deviceSettingGroupId,
    );

    // デバイス設定グループ名に重複があった場合はエラーを出す
    const isDuplicate: boolean = tmpDeviceSettingGroupList.some(
      (deviceSettingGroup) =>
        deviceSettingGroup.deviceSettingGroupName ===
        editedDeviceSettingGroup.deviceSettingGroupName,
    );
    if (isDuplicate) {
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message: `設定グループ名が重複しているため、${
          updating ? '更新' : '追加'
        }に失敗しました。\n別の名前でもう一度お試しください。`,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
      return;
    }

    const deviceIdList = [];
    const editedDeviceList = [];
    if (editedDeviceSettingGroup.groupDeviceList) {
      for (const device of editedDeviceSettingGroup.groupDeviceList) {
        deviceIdList.push(device.deviceId);
        editedDeviceList.push({
          deviceId: device.deviceId,
          deviceName: device.deviceName,
          deviceTypeId: device.deviceTypeId,
          deviceTypeKey: device.deviceTypeKey,
          deviceTypeDisplayName: device.deviceTypeDisplayName,
          tenantId: device.tenantId,
        });
      }
    }

    if (editedDeviceSettingGroup.deviceSettingGroupName === undefined) {
      return;
    }
    const deviceSettingGroupRequest: DeviceSettingGroupUpdateRequest = {
      device_setting_group_name:
        editedDeviceSettingGroup.deviceSettingGroupName,
      device_type_id: editedDeviceSettingGroup.deviceTypeId || '',
      device_type_key: editedDeviceSettingGroup.deviceTypeKey || '',
      description: editedDeviceSettingGroup.description || '',
      device_id_list: deviceIdList,
      deviceList: editedDeviceList,
    };

    // 更新時
    if (groupId) {
      this.deviceSettingService
        .updateDeviceSettingGroup(groupId, deviceSettingGroupRequest)
        .subscribe({
          next: () => {
            this._initDeviceSettingList();
            if (this.role === 'tenant_admin') {
              this._initDeviceSettingGroupList();
            }
          },
          error: (errorResponse) => {
            let message = '';
            if (
              errorResponse.response.error.detail.type ==
              'AlreadyExistsWebException'
            ) {
              message = `設定グループ名が重複しているため、更新に失敗しました。\n別の名前でもう一度お試しください。`;
            } else {
              message =
                '設定グループ更新に失敗しました。\nしばらくしてからもう一度お試しください。';
            }
            const alertDialogOptions: AlertDialogOption = {
              type: 'error',
              message: message,
            };
            this.dialog.open(AlertDialogComponent, {
              width: '600px',
              data: alertDialogOptions,
            });
          },
        });
    }
    // 追加時
    else {
      this.deviceSettingService
        .addDeviceSettingGroup(deviceSettingGroupRequest)
        .subscribe({
          next: () => {
            this._initDeviceSettingList();
            if (this.role === 'tenant_admin') {
              this._initDeviceSettingGroupList();
            }
          },
          error: (errorResponse) => {
            let message = '';
            if (
              errorResponse.response.error.detail.type ==
              'AlreadyExistsWebException'
            ) {
              message = `設定グループ名が重複しているため、追加に失敗しました。\n別の名前でもう一度お試しください。`;
            } else {
              message =
                '設定グループ追加に失敗しました。\nしばらくしてからもう一度お試しください。';
            }
            const alertDialogOptions: AlertDialogOption = {
              type: 'error',
              message: message,
            };
            this.dialog.open(AlertDialogComponent, {
              width: '600px',
              data: alertDialogOptions,
            });
          },
        });
    }
  }

  /** 対象の配列をグループ化する。 */
  private groupList<K extends PropertyKey, V>(
    array: readonly V[],
    getKey: (cur: V, idx: number, src: readonly V[]) => K,
  ) {
    return array.reduce(
      (obj, cur, idx, src) => {
        const key = getKey(cur, idx, src);
        (obj[key] || (obj[key] = []))!.push(cur);
        return obj;
      },
      {} as Partial<Record<K, V[]>>,
    );
  }

  /**
   * 第一ソートキーをデバイスグループ名としてソートする
   */
  private sortDeviceSettingGroupList(
    deviceList: DeviceSettingGroupViewModel[],
  ) {
    deviceList.sort((a, b) => {
      if (a.deviceSettingGroupName < b.deviceSettingGroupName) {
        return -1;
      }
      if (a.deviceSettingGroupName > b.deviceSettingGroupName) {
        return 1;
      }
      return 0;
    });
  }
}
